import { useQuery } from '@tanstack/vue-query'
import type { ComputedRef } from 'vue'
import { computed } from 'vue'

import { Trend } from '@/modules/reports/interfaces/reportData'
import { getHotelReport } from '@/modules/reports/services/hotelReportApi'

interface Params {
	companyId?: number
	filters: Filters
}

interface Filters {
	dates?: string[] | null
	creationDates?: string[] | null
	status?: string
	groupBy?: string
	travelerId?: number
	hotelId?: number
	createdAt?: string[] | null
}

const getCompanyReportHotelsTrends = async (
	params: Params
): Promise<Trend[]> => {
	const { data } = await getHotelReport<Trend[]>(params.companyId!, 'trends', {
		params: {
			checkOutDates: params.filters.dates,
			createdDate: params.filters.creationDates,
			status: params.filters.status,
			groupBy: params.filters.groupBy,
			travelerId: params.filters.travelerId,
			hotelId: params.filters.hotelId,
			createdAt: params.filters.createdAt,
		},
	})

	return data
}

export default function useCompanyReportHotelTrends(
	params: ComputedRef<Params>
) {
	const queryKey = computed(() => [
		'companyReportHotelsTrends',
		params.value.companyId,
		params.value.filters.dates,
		params.value.filters.creationDates,
		params.value.filters.status,
		params.value.filters.groupBy,
		params.value.filters.travelerId,
		params.value.filters.hotelId,
		params.value.filters.createdAt,
	])

	const enabled = computed(() => !!params.value.companyId)

	const { data, isLoading, isFetching, error, refetch } = useQuery({
		queryKey,
		queryFn: () => getCompanyReportHotelsTrends(params.value),
		enabled,
		staleTime: 0,
		cacheTime: 0,
		retry: false,
		refetchOnWindowFocus: false,
	})

	const trends = computed<Trend[] | undefined>(() => data.value)

	return {
		trends,
		isLoading: computed(() => enabled.value && isLoading.value),
		isFetching,
		error,
		refetch,
	}
}
